import type { Id } from './common';

// S8 "Persona của tôi" (spec §4.1, §4.3, §8.3): each person's own assistant. These lists are the single source of the
// persona enums: `029_persona.sql` writes the same literals into its CHECKs and a migration test compares the two. Data
// only; the API's zod schemas take their bounds from here.

/** `appearance.palette`: the eight colours of the character (§8.3). */
export const PALETTES = ['violet', 'teal', 'pink', 'orange', 'leaf', 'sky', 'graphite', 'honey'] as const;
export type Palette = (typeof PALETTES)[number];
/** `appearance.eyes`. */
export const EYES = ['round', 'bean', 'lash'] as const;
export type Eyes = (typeof EYES)[number];
/** `appearance.head`. */
export const HEADS = ['antenna', 'ears', 'tuft'] as const;
export type Head = (typeof HEADS)[number];
/** `appearance.accessory`. */
export const ACCESSORIES = ['none', 'glasses', 'bow', 'hat', 'headphones', 'flower'] as const;
export type Accessory = (typeof ACCESSORIES)[number];

/** `personality.preset`, besides `custom` (any knob moved off its preset's value, §4.3). */
export const PERSONALITY_PRESETS = ['secretary', 'advisor', 'quick', 'buddy'] as const;
export type PersonalityPreset = (typeof PERSONALITY_PRESETS)[number];
/** One personality knob: formal ↔ familiar, brief ↔ detailed, serious ↔ playful. */
export type Knob = 1 | 2 | 3 | 4 | 5;

/** `focus.modules`: the parts of the system a person asks the assistant to keep an eye on. */
export const FOCUS_MODULES = ['workplace', 'projects', 'partners', 'finance', 'strategy'] as const;
export type FocusModule = (typeof FOCUS_MODULES)[number];

/** §4.3: each preset's knobs. */
export const PRESET_KNOBS: Record<PersonalityPreset, { formality: Knob; verbosity: Knob; humor: Knob }> = {
  secretary: { formality: 2, verbosity: 3, humor: 2 },
  advisor: { formality: 2, verbosity: 2, humor: 1 },
  quick: { formality: 3, verbosity: 1, humor: 2 },
  buddy: { formality: 4, verbosity: 3, humor: 4 },
};

export type PersonaAppearance = { palette: Palette; eyes: Eyes; head: Head; accessory: Accessory };
export type PersonaPersonality = { preset: PersonalityPreset | 'custom'; formality: Knob; verbosity: Knob; humor: Knob };
export type PersonaFocus = { modules: FocusModule[]; projectIds: Id[] };

/** The 029 column defaults: what a person with no prefs row reads. */
export const DEFAULT_APPEARANCE = { palette: 'violet', eyes: 'round', head: 'antenna', accessory: 'none' } as const;
/**
 * S8 D111: a look as this build can draw it, in one fixed key order. A key another release added (an open tab on the old
 * bundle, a rollback) is not known here: that axis draws its default part instead of failing the whole screen.
 */
export function knownAppearance(a: Partial<Record<keyof PersonaAppearance, unknown>> | null | undefined): PersonaAppearance {
  const pick = <T extends string>(list: readonly T[], value: unknown, fallback: T): T =>
    (list as readonly unknown[]).includes(value) ? (value as T) : fallback;
  return {
    palette: pick(PALETTES, a?.palette, DEFAULT_APPEARANCE.palette),
    eyes: pick(EYES, a?.eyes, DEFAULT_APPEARANCE.eyes),
    head: pick(HEADS, a?.head, DEFAULT_APPEARANCE.head),
    accessory: pick(ACCESSORIES, a?.accessory, DEFAULT_APPEARANCE.accessory),
  };
}
export const DEFAULT_PERSONALITY = { preset: 'secretary', formality: 2, verbosity: 3, humor: 2 } as const;
export const DEFAULT_PERSONA_NAME = 'Persona';
export const DEFAULT_SELF_NAME = 'em';
/**
 * The common Vietnamese pronouns a person may pick as the assistant's self word (S9 D68). A note to an assignee is
 * written AS the delegator, and such a note naturally uses these words, so a pronoun self word never blocks it; only
 * a custom self word (a name) does. Lower case, NFC.
 */
export const PRONOUN_SELF_NAMES: readonly string[] = Object.freeze(['em', 'anh', 'chị', 'mình', 'tôi', 'tớ', 'tui',
  'con', 'cháu', 'bạn', 'ad', 'ta', 'bé', 'cô', 'chú', 'bác', 'ông', 'bà']);

/** The 029 CHECK bounds: lengths after `btrim`, and the most projects one focus holds. */
export const PERSONA_LIMITS = { name: 40, selfName: 20, instructions: 1000, roleTitle: 80, focusProjects: 20 } as const;

/** `onboarding_step`: step 1 is the existing change-password page (§8.1), so the stored step is 2–6. */
export const ONBOARDING_STEPS = [2, 3, 4, 5, 6] as const;
export type OnboardingStep = (typeof ONBOARDING_STEPS)[number];

export type UserPersona = {
  name: string; selfName: string; appearance: PersonaAppearance; personality: PersonaPersonality;
  instructions: string | null; roleTitle: string | null; focus: PersonaFocus;
};
/** D117: "Nghe thử thật" — real model calls a person may make per business day. */
export const VOICE_SAMPLE_DAILY = 10;
export type OnboardingState = { step: OnboardingStep; deferredAt: string | null; completedAt: string | null };

/** `assistant_settings.scope` (§7): `system` is H-Operation and its connected tools only; `work` adds general work help. */
export const ASSISTANT_SCOPES = ['system', 'work'] as const;
export type AssistantScope = (typeof ASSISTANT_SCOPES)[number];
export const DEFAULT_SCOPE: AssistantScope = 'work';
